class ProductSelector {
    private radios: NodeListOf<HTMLInputElement> | undefined;

    constructor() {
        document.addEventListener("DOMContentLoaded", () => this.init());
    }

    /**
     * Initializes the product selector functionality.
     */
    private init(): void {
        this.radios = document.querySelectorAll('input[type=radio][data-offer-picker]');

        if (!this.radios.length) {
            console.warn("ProductSelector: No product property radios found.");
            return;
        }

        // Attach event listeners to each radio button
        this.radios.forEach((radio) => {
            radio.addEventListener("change", () => this.onPropertyChange());
        });
    }

    /**
     * Handles radio button change event, makes AJAX call, updates partials, and updates the URL.
     */
    private onPropertyChange(): void {
        const selectedProperties = this.getSelectedProperties();
        const productId = this.getProductId();
        const offerId = !selectedProperties ? this.getOfferId() : null;

        if (!productId) {
            console.warn("ProductSelector: Missing product ID.");
            return;
        }

        // Make AJAX request using OctoberCMS framework
        window.oc.ajax("onUpdateOfferSelection", {
            data: {
                item_id: productId,
                offer_id: offerId,
                options: selectedProperties,
            },
            flash: true,
            success: (data: Record<string, any>) => {
                this.updatePartials(data.partials);

                if (selectedProperties)
                    this.updateUrl(selectedProperties);

                this.init();

                window.conversionTrigger.viewItem(data.ct_view_item);
            }
        });
    }

    /**
     * Updates the DOM elements with new content from AJAX response.
     * @param partials - The object containing element IDs as keys and their new HTML as values.
     */
    private updatePartials(partials: Record<string, string>): void {
        Object.entries(partials).forEach(([selector, content]) => {
            const elementId = selector.replace("#", ""); // Remove the # from the selector
            const element = document.getElementById(elementId);

            if (element) {
                element.innerHTML = content;
            } else {
                console.warn(`ProductSelector: Element with ID '${elementId}' not found.`);
            }
        });
    }

    /**
     * Retrieves the currently selected properties from all radio groups.
     * @returns An object with selected properties or null if none are selected.
     */
    private getSelectedProperties(): Record<string, string> | null {
        const selected: Record<string, string> = {};

        this.radios?.forEach((radio) => {
            if (radio.checked) {
                const propertyName = radio.getAttribute("data-property-name");
                const propertyValue = radio.value;

                if (propertyName) {
                    selected[propertyName] = propertyValue;
                }
            }
        });

        return Object.keys(selected).length ? selected : null;
    }

    /**
     * Retrieves the product ID from the first radio input.
     * Assumes all radio inputs belong to the same product.
     *
     * @returns The product ID or null if not found.
     */
    private getProductId(): string | null {
        return this.radios ? this.radios[0]?.getAttribute("data-product-id") : null;
    }

    /**
     * Retrieves the offer ID from the selected radio input.
     * Assumes all radio inputs belong to the same product.
     *
     * @returns The product ID or null if not found.
     */
    private getOfferId(): string | null {

        if (!this.radios || this.radios.length === 0) return null;

        const checkedRadio = Array.from(this.radios).find(radio => radio.checked);

        return checkedRadio?.value ?? null;
    }

    /**
     * Updates the URL query string with the selected properties.
     * @param selectedProperties - The selected property values.
     */
    private updateUrl(selectedProperties: Record<string, string>): void {
        const queryParams = new URLSearchParams(window.location.search);

        // Update each selected property in the URL
        Object.entries(selectedProperties).forEach(([key, value]) => {
            queryParams.set(key, value);
        });

        history.replaceState(null, "", "?" + queryParams.toString());
    }
}

// Initialize ProductSelector
new ProductSelector();